import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    type OnInit,
    type OnChanges,
    type SimpleChanges,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SpFormControlsModule } from '@components/sp-form-controls';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

type WidgetPosition = {
    width?: number;
    tabletWidth?: number;
    mobileWidth?: number;

    x?: number;
    tabletX?: number;
    mobileX?: number;

    y?: number;
    tabletY?: number;
    mobileY?: number;

    rotate?: number;
    tabletRotate?: number;
    mobileRotate?: number;

    zIndex?: number;
    tabletZIndex?: number;
    mobileZIndex?: number;
    // hideOnMobile?: boolean;
    // hideOnDesktop?: boolean;
    // mobileHideOnMobile?: boolean;
    // mobileHideOnDesktop?: boolean;
};

@Component({
    selector: 'web-widget-position-settings',
    templateUrl: './web-widget-position-settings.component.html',
    styleUrls: ['./web-widget-position-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpFormControlsModule, TranslateModule, NgIf, TooltipModule],
})
export class WebWidgetPositionSettingsComponent implements OnInit, OnChanges {
    @Input() public isSynchronized: boolean;
    @Input() public isTablet: boolean;
    @Input() public isMobile: boolean;
    @Input() public widgetId: string;
    @Input() public columnId: string;
    @Input() public position: WidgetPosition;

    @Input() public showWidth: boolean = false;

    @Output() public positionChange: EventEmitter<WidgetPosition> = new EventEmitter();

    private column: HTMLElement;
    private widget: HTMLElement;

    public ngOnInit(): void {
        if (!this.columnId) {
            throw new Error('Column id is required');
        }

        this.column = document.getElementById(this.columnId);
        this.widget = document.getElementById(this.widgetId);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes.isSynchronized?.firstChange && changes.isSynchronized?.currentValue === true) {
            this.positionChange.emit({
                ...this.position,
                mobileRotate: this.position.rotate,
                mobileWidth: this.position.width,
                mobileX: this.position.x,
                mobileY: this.position.y,
                mobileZIndex: this.position.zIndex,
            });
        }
    }

    public onPositionXChange(value: number): void {
        if (this.isSynchronized) {
            this.positionChange.emit({ ...this.position, x: value, mobileX: value });
            return;
        }

        if (this.isTablet) {
            this.positionChange.emit({ ...this.position, tabletX: value });
            return;
        }

        if (this.isMobile) {
            this.positionChange.emit({ ...this.position, mobileX: value });
            return;
        }

        this.positionChange.emit({ ...this.position, x: value });
    }

    public onPositionYChange(value: number): void {
        if (this.isSynchronized) {
            this.positionChange.emit({ ...this.position, y: value, mobileY: value });
            return;
        }

        if (this.isTablet) {
            this.positionChange.emit({ ...this.position, tabletY: value });
            return;
        }

        if (this.isMobile) {
            this.positionChange.emit({ ...this.position, mobileY: value });
            return;
        }

        this.positionChange.emit({ ...this.position, y: value });
    }

    public onZIndexChange(value: number): void {
        if (this.isSynchronized) {
            this.positionChange.emit({ ...this.position, zIndex: value, mobileZIndex: value });
            return;
        }

        if (this.isTablet) {
            this.positionChange.emit({ ...this.position, tabletZIndex: value });
            return;
        }

        if (this.isMobile) {
            this.positionChange.emit({ ...this.position, mobileZIndex: value });
            return;
        }

        this.positionChange.emit({ ...this.position, zIndex: value });
    }

    public onRotationChange(value: number): void {
        if (this.isSynchronized) {
            this.positionChange.emit({ ...this.position, rotate: value, mobileRotate: value });
            return;
        }

        if (this.isTablet) {
            this.positionChange.emit({ ...this.position, tabletRotate: value });
            return;
        }

        if (this.isMobile) {
            this.positionChange.emit({ ...this.position, mobileRotate: value });
            return;
        }

        this.positionChange.emit({ ...this.position, rotate: value });
    }

    // public onHideOnMobileChange(value: boolean): void {
    //     if (this.isSynchronized && !this.isMobile) {
    //         this.positionChange.emit({ ...this.position, hideOnMobile: value, mobileHideOnMobile: value });
    //         return;
    //     }
    //
    //     this.positionChange.emit({ ...this.position, hideOnMobile: value });
    // }
    //
    // public onHideOnDesktopChange(value: boolean): void {
    //     if (this.isSynchronized && !this.isMobile) {
    //         this.positionChange.emit({ ...this.position, hideOnDesktop: value, mobileHideOnDesktop: value });
    //         return;
    //     }
    //
    //     this.positionChange.emit({ ...this.position, hideOnDesktop: value });
    // }

    public onWidthChange(value: number): void {
        if (!this.showWidth) {
            this.positionChange.emit({ ...this.position, width: value });
            return;
        }

        if (this.isSynchronized) {
            this.positionChange.emit({ ...this.position, width: value, mobileWidth: value });
            return;
        }

        if (this.isTablet) {
            this.positionChange.emit({ ...this.position, tabletWidth: value });
            return;
        }

        if (this.isMobile) {
            this.positionChange.emit({ ...this.position, mobileWidth: value });
            return;
        }

        this.positionChange.emit({ ...this.position, width: value });
    }

    public get positionX(): number {
        if (this.isTablet && !this.isSynchronized) {
            return this.position.tabletX ?? this.position.x ?? 0;
        }

        if (this.isMobile && !this.isSynchronized) {
            return this.position.mobileX ?? 0;
        }

        return this.position.x ?? 0;
    }

    public get positionY(): number {
        if (this.isTablet && !this.isSynchronized) {
            return this.position.tabletY ?? this.position.y ?? 0;
        }

        if (this.isMobile && !this.isSynchronized) {
            return this.position.mobileY ?? 0;
        }

        return this.position.y ?? 0;
    }

    public get zIndex(): number {
        if (this.isTablet && !this.isSynchronized) {
            return this.position.tabletZIndex ?? this.position.zIndex ?? 0;
        }

        if (this.isMobile && !this.isSynchronized) {
            return this.position.mobileZIndex ?? 0;
        }

        return this.position.zIndex ?? 0;
    }

    public get rotation(): number {
        if (this.isTablet && !this.isSynchronized) {
            return this.position.tabletRotate ?? this.position.rotate ?? 0;
        }

        if (this.isMobile && !this.isSynchronized) {
            return this.position.mobileRotate ?? 0;
        }

        return this.position.rotate ?? 0;
    }

    public get width(): number {
        if (!this.showWidth) {
            return this.position.width ?? 100;
        }

        if (this.isTablet && !this.isSynchronized) {
            return this.position.tabletWidth ?? this.position.width ?? 100;
        }

        if (this.isMobile && !this.isSynchronized) {
            return this.position.mobileWidth ?? 100;
        }

        return this.position.width ?? 100;
    }
}
